import { Center, Loader, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import EnergySpotComparison from 'components/EnergySpotComparison/EnergySpotComparison';
import { useEnergyConsumptionData } from 'pages/tuya-device/hooks/useEnergyConsumptionData';
import EnergyConsumptionFilters from 'pages/tuya-device/components/EnergyConsumptionFilters';
import ConsumptionChartCard from 'pages/tuya-device/components/ConsumptionChartCard';
import SpotCostChartCard from 'pages/tuya-device/components/SpotCostChartCard';
import FixedTariffChartCard from 'pages/tuya-device/components/FixedTariffChartCard';
import CostDifferenceChartCard from 'pages/tuya-device/components/CostDifferenceChartCard';
import SpotPricesChartCard from 'pages/tuya-device/components/SpotPricesChartCard';
import MonthlySavingsChartCard from 'pages/tuya-device/components/MonthlySavingsChartCard';
import HourlyHistogramCard from 'pages/tuya-device/components/HourlyHistogramCard';

/**
 * Energy Consumption implementation component — layout orchestrator.
 * All data fetching and computed values live in useEnergyConsumptionData.
 */
export function EnergyConsumptionImpl({ deviceId }: { deviceId: number }) {
  const {
    data,
    monthlyStats,
    selectedDate,
    setSelectedDate,
    fixedTariff,
    setFixedTariff,
    spotPrices,
    totalConsumption,
    validIntervalLabels,
    costData,
    totalCost,
    fixedTariffCostData,
    totalFixedTariffCost,
    differenceData,
    sharedCostYMax,
  } = useEnergyConsumptionData(deviceId);

  if (!data) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="lg" color="#551FFF" />
      </Center>
    );
  }

  return (
    <Stack bg="gray.0" py={20} px={16} mih="100vh">
      <EnergyConsumptionFilters
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        fixedTariff={fixedTariff}
        setFixedTariff={setFixedTariff}
      />

      <ConsumptionChartCard data={data} totalConsumption={totalConsumption} />

      {costData && spotPrices.length > 0 && totalCost !== null && (
        <SpotCostChartCard
          costData={costData}
          validIntervalLabels={validIntervalLabels}
          totalCost={totalCost}
          spotPrices={spotPrices}
          date={data.date}
          sharedCostYMax={sharedCostYMax}
        />
      )}

      {fixedTariffCostData && totalFixedTariffCost !== null && (
        <FixedTariffChartCard
          fixedTariffCostData={fixedTariffCostData}
          validIntervalLabels={validIntervalLabels}
          totalFixedTariffCost={totalFixedTariffCost}
          totalCost={totalCost}
          fixedTariff={fixedTariff}
          date={data.date}
          sharedCostYMax={sharedCostYMax}
        />
      )}

      {differenceData && totalCost !== null && totalFixedTariffCost !== null && (
        <CostDifferenceChartCard
          differenceData={differenceData}
          validIntervalLabels={validIntervalLabels}
          totalCost={totalCost}
          totalFixedTariffCost={totalFixedTariffCost}
          date={data.date}
        />
      )}

      {spotPrices.length > 0 && <SpotPricesChartCard spotPrices={spotPrices} date={data.date} />}

      {monthlyStats && monthlyStats.dailySavings && monthlyStats.dailySavings.length > 0 && (
        <MonthlySavingsChartCard monthlyStats={monthlyStats} />
      )}

      {monthlyStats && monthlyStats.hourlyHistogram && monthlyStats.hourlyHistogram.length > 0 && (
        <HourlyHistogramCard monthlyStats={monthlyStats} />
      )}

      <EnergySpotComparison data={data} selectedDate={selectedDate} deviceId={deviceId} />
    </Stack>
  );
}

/**
 * Energy Consumption page - displays energy consumption vs spot prices
 */
export default function EnergyConsumption() {
  const { deviceId: deviceIdRaw = '' } = useParams();
  const deviceId = useMemo(() => {
    const parsedDeviceId = parseInt(deviceIdRaw);
    return parsedDeviceId;
  }, [deviceIdRaw]);

  return <EnergyConsumptionImpl deviceId={deviceId} />;
}
